import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const contactInfo = [
  { icon: 'ri-mail-send-line', label: 'Editorial', value: 'editorial@callofduty next.com' },
  { icon: 'ri-news-line', label: 'Press Inquiries', value: 'press@callofduty next.com' },
  { icon: 'ri-advertisement-line', label: 'Advertising', value: 'ads@callofduty next.com' },
  { icon: 'ri-twitter-x-line', label: 'Twitter / X', value: '@CallOfDutyNext' },
];

const contactSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact CallOfDutyNext.com — Tips, Press & Advertising',
    description:
      'Get in touch with CallOfDutyNext.com for news tips, press inquiries, advertising, corrections, or general questions.',
    url: `${SITE_URL}/contact`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
    ],
  },
];

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    fetch('https://readdy.ai/api/form/d70pm2pqa83q0hmd1f10', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => { setSubmitted(true); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const inputClass = "w-full bg-[#0a0a0a] border border-[#222] focus:border-[#444] text-white text-sm px-4 py-3 outline-none placeholder-[#444] transition-colors font-['Barlow_Condensed']";

  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Contact CallOfDutyNext.com — News Tips, Press & Advertising"
        description="Contact the CallOfDutyNext.com team for news tips, press inquiries, advertising opportunities, partnership requests, or general questions. We protect all sources."
        keywords="contact CallOfDutyNext, CoD news tips, press inquiry, advertising CoD site"
        canonical="/contact"
        schema={contactSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* ── Title bar ─────────────────────────── */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
              Contact
            </h1>
            <p className="text-[#555] text-sm mt-1">
              Got a tip, press inquiry, or just want to talk CoD? We&apos;re all ears.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-10">

            {/* ── LEFT sidebar ────────────────────── */}
            <div className="lg:col-span-1 mb-8 lg:mb-0">
              {/* Contact info */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] mb-6">
                <div className="px-5 py-4 border-b border-[#1e1e1e]">
                  <h2 className="font-['Barlow_Condensed'] font-black text-white text-sm uppercase tracking-widest">
                    Contact Info
                  </h2>
                </div>
                <div className="divide-y divide-[#1e1e1e]">
                  {contactInfo.map((item) => (
                    <div key={item.label} className="flex items-center gap-4 px-5 py-4">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0 text-[#555]">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <div>
                        <div className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase">
                          {item.label}
                        </div>
                        <div className="text-[#aaa] text-sm mt-0.5">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tip line */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Tag label="TIP LINE" />
                </div>
                <h3 className="font-['Barlow_Condensed'] font-black text-white text-base uppercase mb-2">
                  Got Inside Intel?
                </h3>
                <p className="text-[#666] text-sm leading-relaxed mb-4">
                  Data miners, insiders, community members — if you&apos;ve got something juicy, we protect all sources. No exceptions.
                </p>
                <div className="flex items-center gap-2 text-[#00ff41] text-sm font-['Barlow_Condensed'] font-bold">
                  <i className="ri-lock-2-line" />
                  tips@callofduty next.com
                </div>
              </div>
            </div>

            {/* ── RIGHT form ──────────────────────── */}
            <div className="lg:col-span-2">
              <div className="border border-[#1e1e1e] bg-[#0d0d0d]">
                <div className="px-8 py-4 border-b border-[#1e1e1e] flex items-center justify-between">
                  <h2 className="font-['Barlow_Condensed'] font-black text-white text-sm uppercase tracking-widest">
                    Send a Message
                  </h2>
                  <Tag label="OPEN" />
                </div>

                {submitted ? (
                  <div className="text-center py-20 px-8">
                    <i className="ri-checkbox-circle-line text-[#00ff41] text-5xl mb-4 block" />
                    <h3 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase mb-2">
                      Message Received
                    </h3>
                    <p className="text-[#666] text-sm">We&apos;ll get back to you within 24 hours. Stay frosty.</p>
                  </div>
                ) : (
                  <form
                    id="contact-page-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="p-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your callsign"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className={inputClass}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className={`${inputClass} cursor-pointer`}
                      >
                        <option value="" disabled>Select a subject</option>
                        <option value="News Tip">News Tip</option>
                        <option value="Press Inquiry">Press Inquiry</option>
                        <option value="Advertising">Advertising</option>
                        <option value="Correction Request">Correction Request</option>
                        <option value="Partnership">Partnership</option>
                        <option value="General">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase">
                          Message *
                        </label>
                        <span className={`text-xs font-['Barlow_Condensed'] font-bold ${charCount > 500 ? 'text-red-500' : 'text-[#444]'}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Drop your message here..."
                        className={`${inputClass} resize-none`}
                      />
                      {charCount > 500 && (
                        <p className="text-red-500 text-xs mt-1">Message must be 500 characters or less.</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={loading || charCount > 500}
                      className="w-full bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-widest py-4 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                    >
                      {loading ? 'SENDING...' : 'SEND MESSAGE'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
